import { useEffect, useState } from "react";
import { Download, LogOut, Users, HandHeart, Loader2, Eye, BarChart3, Trash2, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { adminLogin, getSupporters, getStats, exportSupporters, getEvents, deleteEvent, createEvent, updateEvent } from "@/lib/api";
import { RESOLUTIONS } from "@/data/resolutions";
import { PETITIONS } from "@/data/petitions";
import WolfCrest from "@/components/WolfCrest";

const SECTION_LABELS = {
  story: "Story — Dave's journey",
  platform: "Platform — 16 commitments",
  "day-one": "Resolution Solutions (list)",
  referendum: "Referendum Solutions (list)",
  film: "Campaign film",
  gallery: "Photo gallery",
  events: "Events",
  vote: "Voter info",
  volunteer: "Volunteer",
  signup: "Sign-up form",
};

const viewLabel = (key) => {
  const [type, slug] = key.split(":");
  if (type === "section") return SECTION_LABELS[slug] || slug;
  if (type === "resolution") {
    const r = RESOLUTIONS.find((x) => x.slug === slug);
    return r ? `Resolution §${r.n} — ${r.title}` : slug;
  }
  if (type === "petition") {
    const p = PETITIONS.find((x) => x.slug === slug);
    return p ? `Petition ${p.n} — ${p.title}` : slug;
  }
  return key;
};

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [supporters, setSupporters] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0 });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [editing, setEditing] = useState(null); // event id or "new"
  const [draft, setDraft] = useState({ title: "", date: "", time: "", location: "", description: "" });
  const [savingEvent, setSavingEvent] = useState(false);

  const startEdit = (e) => {
    setEditing(e.id);
    setDraft({ title: e.title, date: e.date, time: e.time, location: e.location, description: e.description });
  };

  const startNewEvent = () => {
    setEditing("new");
    setDraft({ title: "", date: "", time: "", location: "", description: "" });
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setSavingEvent(true);
    try {
      if (editing === "new") {
        const r = await createEvent(token, draft);
        setEvents((ev) => [...ev, r.data].sort((a, b) => a.date.localeCompare(b.date)));
        toast.success("Event added.");
      } else {
        await updateEvent(token, editing, draft);
        setEvents((ev) =>
          ev.map((x) => (x.id === editing ? { ...x, ...draft } : x)).sort((a, b) => a.date.localeCompare(b.date))
        );
        toast.success("Event updated.");
      }
      setEditing(null);
    } catch {
      toast.error("Could not save event.");
    } finally {
      setSavingEvent(false);
    }
  };

  const load = async (t) => {
    setListLoading(true);
    try {
      const [r, s, ev] = await Promise.all([getSupporters(t), getStats(t), getEvents()]);
      setSupporters(r.data);
      setStats(s.data);
      setEvents(ev.data);
    } catch {
      sessionStorage.removeItem("admin_token");
      setToken("");
      toast.error("Session expired — please sign in again.");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await adminLogin(password);
      sessionStorage.setItem("admin_token", r.data.token);
      setToken(r.data.token);
    } catch {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  const removeEvent = async (id) => {
    try {
      await deleteEvent(token, id);
      setEvents((ev) => ev.filter((e) => e.id !== id));
      toast.success("Event removed.");
    } catch {
      toast.error("Could not remove event.");
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await exportSupporters(token);
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "supporters.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
    setSupporters([]);
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-navy noise relative flex items-center justify-center px-5" data-testid="admin-login">
        <div className="w-full max-w-sm relative z-10">
          <div className="flex flex-col items-center mb-8 text-[#F8F9FA]">
            <WolfCrest className="w-14 h-14 mb-4" />
            <h1 className="font-serif text-2xl font-bold">Campaign Admin</h1>
            <p className="text-steel text-sm mt-1">Supporter list access</p>
          </div>
          <form onSubmit={login} className="bg-[#F8F9FA] rounded-sm p-7 space-y-4" data-testid="admin-login-form">
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="h-12 rounded-sm border-navy/20"
              data-testid="admin-password-input"
              aria-label="Admin password"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-electric hover:bg-electric-hover text-white text-sm uppercase tracking-[0.2em] font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </div>
      </main>
    );
  }

  const volunteers = supporters.filter((s) => s.volunteer).length;

  return (
    <main className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      <header className="bg-navy text-[#F8F9FA] px-5 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <WolfCrest className="w-8 h-8" />
          <div>
            <p className="font-serif font-bold text-sm">Campaign Admin</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-steel">Supporter List</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadCsv}
            className="bg-electric hover:bg-electric-hover text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full flex items-center gap-2 transition-colors"
            data-testid="admin-export-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={logout}
            className="text-steel hover:text-white p-2 transition-colors"
            data-testid="admin-logout-btn"
            aria-label="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-navy/10 border border-navy/10 mb-8 max-w-3xl">
          <div className="bg-white p-5" data-testid="admin-total-count">
            <div className="flex items-center gap-2 text-steel text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">
              <Users className="w-3.5 h-3.5" /> Supporters
            </div>
            <p className="font-serif text-3xl font-black text-navy">{supporters.length}</p>
          </div>
          <div className="bg-white p-5" data-testid="admin-volunteer-count">
            <div className="flex items-center gap-2 text-steel text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">
              <HandHeart className="w-3.5 h-3.5" /> Volunteers
            </div>
            <p className="font-serif text-3xl font-black text-electric">{volunteers}</p>
          </div>
          <div className="bg-white p-5" data-testid="admin-visits-today">
            <div className="flex items-center gap-2 text-steel text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">
              <Eye className="w-3.5 h-3.5" /> Visits Today
            </div>
            <p className="font-serif text-3xl font-black text-navy">{stats.today}</p>
          </div>
          <div className="bg-white p-5" data-testid="admin-visits-total">
            <div className="flex items-center gap-2 text-steel text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">
              <BarChart3 className="w-3.5 h-3.5" /> Total Visits
            </div>
            <p className="font-serif text-3xl font-black text-electric">{stats.total}</p>
          </div>
        </div>

        {listLoading ? (
          <div className="flex items-center gap-2 text-steel py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading supporters…
          </div>
        ) : supporters.length === 0 ? (
          <p className="text-steel text-center py-16" data-testid="admin-empty">No sign-ups yet.</p>
        ) : (
          <div className="overflow-x-auto border border-navy/10 bg-white" data-testid="admin-table-wrap">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="border-b border-navy/10 text-left text-[10px] uppercase tracking-[0.2em] text-steel">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Volunteer</th>
                  <th className="px-4 py-3 font-semibold">Signed Up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/5">
                {supporters.map((s) => (
                  <tr key={s.id} className="hover:bg-navy/[0.03] transition-colors" data-testid={`admin-row-${s.id}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{s.name}</td>
                    <td className="px-4 py-3 text-navy/70">{s.email}</td>
                    <td className="px-4 py-3 text-navy/70">{s.phone || "—"}</td>
                    <td className="px-4 py-3">
                      {s.volunteer ? (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-electric bg-electric/10 px-2 py-1 rounded-full">
                          Yes
                        </span>
                      ) : (
                        <span className="text-steel">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-steel text-xs">
                      {new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          <h3 className="text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mb-4">Visit History — last 30 days</h3>
          <div className="border border-navy/10 bg-white mb-10" data-testid="admin-visit-history">
            {(stats.history || []).length === 0 ? (
              <p className="text-steel text-sm px-4 py-6 text-center" data-testid="admin-visit-history-empty">No visits recorded yet.</p>
            ) : (
              <div className="divide-y divide-navy/5">
                {(stats.history || []).map((d) => {
                  const max = Math.max(...(stats.history || []).map((h) => h.count), 1);
                  return (
                    <div key={d.date} className="flex items-center gap-4 px-4 py-2.5" data-testid={`admin-visit-day-${d.date}`}>
                      <span className="text-xs font-semibold text-navy w-24 shrink-0">
                        {new Date(`${d.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", weekday: "short" })}
                      </span>
                      <div className="flex-1 h-2 bg-navy/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-electric rounded-full transition-[width] duration-500"
                          style={{ width: `${Math.max((d.count / max) * 100, 4)}%` }}
                        />
                      </div>
                      <span className="text-sm font-serif font-black text-navy w-10 text-right">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <h3 className="text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mt-12 mb-4">What People Are Reading</h3>
          <div className="border border-navy/10 bg-white mb-10" data-testid="admin-view-stats">
            {(stats.views || []).length === 0 ? (
              <p className="text-steel text-sm px-4 py-6 text-center" data-testid="admin-view-stats-empty">No section views recorded yet.</p>
            ) : (
              <div className="divide-y divide-navy/5">
                {(stats.views || []).map((v) => {
                  const max = Math.max(...(stats.views || []).map((x) => x.count), 1);
                  return (
                    <div key={v.key} className="flex items-center gap-4 px-4 py-2.5" data-testid={`admin-view-${v.key}`}>
                      <span className="text-xs font-semibold text-navy flex-1 truncate">{viewLabel(v.key)}</span>
                      <div className="w-32 md:w-48 h-2 bg-navy/5 rounded-full overflow-hidden shrink-0">
                        <div
                          className="h-full bg-electric rounded-full transition-[width] duration-500"
                          style={{ width: `${Math.max((v.count / max) * 100, 4)}%` }}
                        />
                      </div>
                      <span className="text-sm font-serif font-black text-navy w-10 text-right">{v.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-12 mb-4">
            <h3 className="text-xs uppercase tracking-[0.3em] font-semibold text-navy/60">Events</h3>
            <button
              onClick={startNewEvent}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-electric hover:text-electric-hover transition-colors"
              data-testid="add-event-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add event
            </button>
          </div>
          <div className="border border-navy/10 bg-white divide-y divide-navy/5 mb-10" data-testid="admin-events">
            {editing === "new" && (
              <form onSubmit={saveEvent} className="px-4 py-4 space-y-3 bg-electric/[0.04]" data-testid="event-form-new">
                <div className="grid md:grid-cols-2 gap-3">
                  <Input required minLength={2} placeholder="Event title" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-title" />
                  <Input required type="date" value={draft.date} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-date" />
                  <Input placeholder="Time (e.g. 5:00 – 8:00 PM)" value={draft.time} onChange={(e) => setDraft((d) => ({ ...d, time: e.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-time" />
                  <Input placeholder="Venue, City, State" value={draft.location} onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-location" />
                </div>
                <Input placeholder="Short description (optional)" value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-description" />
                <div className="flex gap-3">
                  <button type="submit" disabled={savingEvent} className="bg-electric hover:bg-electric-hover disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors flex items-center gap-2" data-testid="event-form-save">
                    {savingEvent && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save event
                  </button>
                  <button type="button" onClick={() => setEditing(null)} className="text-steel hover:text-navy text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors flex items-center gap-1.5" data-testid="event-form-cancel">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </form>
            )}
            {events.map((e) => (
              <div key={e.id} data-testid={`admin-event-${e.id}`}>
                {editing === e.id ? (
                  <form onSubmit={saveEvent} className="px-4 py-4 space-y-3 bg-electric/[0.04]" data-testid={`event-form-${e.id}`}>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Input required minLength={2} placeholder="Event title" value={draft.title} onChange={(x) => setDraft((d) => ({ ...d, title: x.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-title" />
                      <Input required type="date" value={draft.date} onChange={(x) => setDraft((d) => ({ ...d, date: x.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-date" />
                      <Input placeholder="Time (e.g. 5:00 – 8:00 PM)" value={draft.time} onChange={(x) => setDraft((d) => ({ ...d, time: x.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-time" />
                      <Input placeholder="Venue, City, State" value={draft.location} onChange={(x) => setDraft((d) => ({ ...d, location: x.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-location" />
                    </div>
                    <Input placeholder="Short description (optional)" value={draft.description} onChange={(x) => setDraft((d) => ({ ...d, description: x.target.value }))} className="h-10 rounded-sm border-navy/20" data-testid="event-form-description" />
                    <div className="flex gap-3">
                      <button type="submit" disabled={savingEvent} className="bg-electric hover:bg-electric-hover disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors flex items-center gap-2" data-testid="event-form-save">
                        {savingEvent && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save event
                      </button>
                      <button type="button" onClick={() => setEditing(null)} className="text-steel hover:text-navy text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors flex items-center gap-1.5" data-testid="event-form-cancel">
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <div className="text-sm">
                      <span className="font-semibold text-navy">{e.title}</span>
                      <span className="text-steel ml-3">{e.date}{e.time ? ` · ${e.time}` : ""} · {e.location}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEdit(e)}
                        className="text-steel hover:text-electric hover:bg-electric/10 p-2 rounded-full transition-colors"
                        data-testid={`edit-event-${e.id}`}
                        aria-label={`Edit ${e.title}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeEvent(e.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-colors"
                        data-testid={`delete-event-${e.id}`}
                        aria-label={`Remove ${e.title}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
      </div>
    </main>
  );
}
